import React, { useRef, useEffect } from "react"
import {
  forceSimulation,
  forceX,
  forceY,
  forceCollide,
  forceCenter,
  forceManyBody,
} from "d3-force"
import { scaleSqrt } from "d3-scale"
import { drag } from "d3-drag"
import { select, event as d3Event } from "d3-selection"
import classnames from "classnames"
import { data } from "./data"
import "./SkillsChart.scss"

const onDrag = simulation => {
  function dragstarted(d) {
    if (!d3Event.active)
      simulation.current
        .alphaTarget(1)
        .alpha(0.4)
        .restart()
    d.fx = d.x
    d.fy = d.y
  }

  function dragged(d) {
    d.fx = d3Event.x
    d.fy = d3Event.y
  }

  function dragended(d) {
    if (!d3Event.active) simulation.current.alphaTarget(0)
    d.fx = null
    d.fy = null
  }

  return drag()
    .on("start", dragstarted)
    .on("drag", dragged)
    .on("end", dragended)
}

function SkillsChart({ width, height, theme }) {
  const maxRadius = width < 350 ? 40 : 80
  const rScale = useRef(
    scaleSqrt()
      .domain([0, 100])
      .range([0, maxRadius])
  )
  const simulation = useRef(
    forceSimulation(data)
      .force("x", forceX().strength(0.05))
      .force("y", forceY().strength(0.05))
      .force(
        "collide",
        forceCollide().radius(dataItem => rScale.current(dataItem.radiusFactor))
      )
      .force("charge", forceManyBody())
      .force("center", forceCenter(width / 2, height / 2))
    // .alphaTarget(0.9)
  )
  const svg = useRef()
  const skillBubble = useRef()

  useEffect(() => {
    // const maxRadius = width < 350 ? 40 : 80
    if (rScale.current.range()[1] !== maxRadius) {
      rScale.current = rScale.current.range([0, maxRadius])
      skillBubble.current
        .selectAll("circle")
        .attr("r", dataItem => rScale.current(dataItem.radiusFactor))
      skillBubble.current
        .selectAll("text")
        .attr("font-size", maxRadius === 60 ? "12px" : "10px")
    }
  }, [maxRadius])

  useEffect(() => {
    if (!svg.current) {
      svg.current = select(".skillset")

      // create a layer or group
      skillBubble.current = svg.current.selectAll(".skillBubble").data(data)
      skillBubble.current.exit().remove()

      let bubble = skillBubble.current
        .enter()
        .append("g")
        .classed("skillBubble", true)
        .attr("id", dataItem => dataItem.id)

      bubble
        .append("circle")
        .attr("r", dataItem => rScale.current(dataItem.radiusFactor))
        .attr("class", dataItem =>
          classnames(dataItem.className, "has-svg-stroke")
        )
      // .attr("fill-opacity", 0.5)

      let text = bubble.append("text")

      text
        .text(dataItem => dataItem.name)
        .style("text-anchor", "middle")
        .attr("dominant-baseline", "central")
        .attr("font-family", "sans-serif")
        .attr("font-size", "12px")
        .attr("font-weight", "normal")
      // .attr("fill", "white")

      skillBubble.current = skillBubble.current.merge(bubble)
      skillBubble.current.call(onDrag(simulation))

      simulation.current.on("tick", () => {
        skillBubble.current.attr(
          "transform",
          dataItem => `translate(${dataItem.x}, ${dataItem.y})`
        )
      })
    } else {
      simulation.current.stop()
      simulation.current = simulation.current
        .force(
          "collide",
          forceCollide().radius(dataItem =>
            rScale.current(dataItem.radiusFactor)
          )
        )
        .force("center", forceCenter(width / 2, height / 2))
      simulation.current.alpha(0.2).restart()
    }
  }, [height, width])

  useEffect(() => {
    skillBubble.current
      .selectAll("circle")
      .attr("fill-opacity", theme === "light" ? 0.5 : 0.8)
      .attr("class", dataItem =>
        classnames(
          dataItem.className,
          theme === "light" ? "has-svg-stroke" : "has-svg-stroke-dark"
        )
      )
  }, [theme])

  return <svg x={0} y={0} width={width} height={height} className="skillset" />
}

export default SkillsChart
